import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ProgressProvider } from "@/context/ProgressContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { PlayerAuthProvider, usePlayerAuth } from "@/context/PlayerAuthContext";
import SplitDemo from "@/pages/SplitDemo";
import PrintCard from "@/pages/PrintCard";
import PrintFlyer from "@/pages/PrintFlyer";
import PrintAd from "@/pages/PrintAd";
import PrintCIAd from "@/pages/PrintCIAd";
import PrintReview from "@/pages/PrintReview";
import PrintDossier from "@/pages/PrintDossier";
import PrintSponsorJournal from "@/pages/PrintSponsorJournal";
import PrintSponsorPlacements from "@/pages/PrintSponsorPlacements";
import PrintCurriculum from "@/pages/PrintCurriculum";
import PrintFoundationGuide from "@/pages/PrintFoundationGuide";
import PrintFieldCard from "@/pages/PrintFieldCard";
import PrintOnboardingGuide from "@/pages/PrintOnboardingGuide";
import PrintReportCard from "@/pages/PrintReportCard";
import PrintCheatSheet from "@/pages/PrintCheatSheet";
import PrintMatrix from "@/pages/PrintMatrix";
import CertificatePage from "@/pages/CertificatePage";
import Dossier from "@/pages/Dossier";
import Film from "@/pages/Film";
import PrintLogo from "@/pages/PrintLogo";
import Cover from "@/pages/Cover";
import ComingSoon from "@/pages/ComingSoon";
import TheSystem from "@/pages/TheSystem";
import HighlightPage from "@/pages/HighlightPage";
import MemberDashboard from "@/pages/MemberDashboard";
import BlueprintPortal from "@/pages/BlueprintPortal";
import TrialClub from "@/pages/TrialClub";
import PrintTrialFlyer from "@/pages/PrintTrialFlyer";
import PrintPeoriaFlyer from "@/pages/PrintPeoriaFlyer";
import TrialPeoriaFC from "@/pages/TrialPeoriaFC";
import Reader from "@/pages/Reader";
import Coach from "@/pages/Coach";
import Hub from "@/pages/Hub";
import Recaps from "@/pages/Recaps";
import RecapView from "@/pages/RecapView";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import InstitutionDashboard from "@/pages/InstitutionDashboard";
import FreeDrills from "@/pages/FreeDrills";
import Unsubscribe from "@/pages/Unsubscribe";
import Roadmap from "@/pages/Roadmap";
import ProfileSettings from "@/pages/ProfileSettings";
import Claim from "@/pages/Claim";
import ClubDashboard from "@/pages/ClubDashboard";
import CoachSetup from "@/pages/CoachSetup";
import CoachClubDashboard from "@/pages/CoachClubDashboard";
import Redeem from "@/pages/Redeem";
import Renew from "@/pages/Renew";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Subscribe from "@/pages/Subscribe";
import ForSchools from "@/pages/ForSchools";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import InstallBanner from "@/components/InstallBanner";
import BottomTabBar from "@/components/BottomTabBar";
import ProfileGate from "@/components/ProfileGate";
import ErrorBoundary from "@/components/ErrorBoundary";
import MobileErgonomics from "@/components/MobileErgonomics";

const Protected = ({ children, allowCoach }) => {
  const { hasAccess, loading } = usePlayerAuth();
  const coachPreview = !!localStorage.getItem("coachToken");
  const ok = allowCoach ? (hasAccess || coachPreview) : hasAccess;
  if (loading) return <div className="min-h-screen bg-[#111] flex items-center justify-center text-white/40">Loading…</div>;
  if (!ok) return <Navigate to="/" replace />;
  return children;
};

// Paying members (Technical Track / Elite) land in the member dashboard; Blueprint-only buyers in the Blueprint portal.
export const homeFor = (player) => (["academy", "elite", "trial"].includes(player?.tier) ? "/dashboard" : "/blueprint");

const Home = () => {
  const { hasAccess, loading, player } = usePlayerAuth();
  if (loading) return <div className="min-h-screen bg-[#111] flex items-center justify-center text-white/40">Loading…</div>;
  if (hasAccess) return <Navigate to={homeFor(player)} replace />;
  return <ComingSoon />;
};

const MemberOnly = ({ children, portal }) => {
  const { hasAccess, loading, player } = usePlayerAuth();
  if (loading) return <div className="min-h-screen bg-[#111] flex items-center justify-center text-white/40">Loading…</div>;
  if (!hasAccess) return <Navigate to="/" replace />;
  const want = homeFor(player);
  if ((portal && want !== "/blueprint") || (!portal && want !== "/dashboard")) return <Navigate to={want} replace />;
  return children;
};

function App() {
  return (
    <PlayerAuthProvider>
      <SettingsProvider>
        <ProgressProvider>
          <ErrorBoundary>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/redeem" element={<Redeem />} />
              <Route path="/renew/:token" element={<Renew />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/subscribe" element={<Subscribe />} />
              <Route path="/for-schools" element={<ForSchools />} />
              <Route path="/the-system" element={<TheSystem />} />
              <Route path="/trial/club" element={<TrialClub audience="club" />} />
              <Route path="/trial/school" element={<TrialClub audience="school" />} />
              <Route path="/trial/peoria-fc" element={<TrialPeoriaFC />} />
              <Route path="/print/peoria-flyer" element={<PrintPeoriaFlyer />} />
              <Route path="/print/peoria-flyer/social" element={<PrintPeoriaFlyer social />} />
              <Route path="/print/trial-flyer/:days" element={<PrintTrialFlyer />} />
              <Route path="/print/trial-flyer/:days/social" element={<PrintTrialFlyer social />} />
              <Route path="/print/trial-flyer/:audience/:days" element={<PrintTrialFlyer />} />
              <Route path="/print/trial-flyer/:audience/:days/social" element={<PrintTrialFlyer social />} />
              <Route path="/store" element={<Cover />} />
              <Route path="/login" element={<Login />} />
              <Route path="/free-drills" element={<FreeDrills />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/institution-dashboard" element={<InstitutionDashboard />} />
              <Route path="/read/:chapterId" element={<Protected allowCoach><Reader /></Protected>} />
              <Route path="/hub" element={<Protected><Hub /></Protected>} />
              <Route path="/recaps" element={<Protected><Recaps /></Protected>} />
              <Route path="/recaps/:sid" element={<Protected><RecapView /></Protected>} />
              <Route path="/dashboard" element={<MemberOnly><MemberDashboard /></MemberOnly>} />
              <Route path="/blueprint" element={<MemberOnly portal><BlueprintPortal /></MemberOnly>} />
              <Route path="/roadmap" element={<Protected><Roadmap /></Protected>} />
              <Route path="/profile" element={<Protected><ProfileSettings /></Protected>} />
              <Route path="/coach" element={<Coach />} />
              <Route path="/club-login" element={<Navigate to="/login" replace />} />
              <Route path="/club-dashboard" element={<ClubDashboard />} />
              <Route path="/coach-setup" element={<CoachSetup />} />
              <Route path="/coach/club-view" element={<CoachClubDashboard />} />
              <Route path="/claim" element={<Claim />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/print/flyer/:id" element={<PrintFlyer />} />
              <Route path="/print/ad" element={<PrintAd />} />
              <Route path="/print/ci-ad" element={<PrintCIAd />} />
              <Route path="/print/review/:i/:fmt" element={<PrintReview />} />
              <Route path="/print/dossier" element={<PrintDossier />} />
              <Route path="/print/sponsor-journal" element={<PrintSponsorJournal />} />
              <Route path="/print/sponsor-placements" element={<PrintSponsorPlacements />} />
              <Route path="/print/curriculum" element={<PrintCurriculum />} />
              <Route path="/print/foundation-guide" element={<PrintFoundationGuide />} />
              <Route path="/print/field-card" element={<PrintFieldCard />} />
              <Route path="/print/onboarding-guide" element={<PrintOnboardingGuide />} />
              <Route path="/print/report-card" element={<PrintReportCard />} />
              <Route path="/print/cheat-sheet" element={<PrintCheatSheet />} />
              <Route path="/print/matrix/:variant" element={<PrintMatrix />} />
              <Route path="/print/film/:film" element={<Film />} />
              <Route path="/print/logo/:variant" element={<PrintLogo />} />
              <Route path="/certificate/:id" element={<CertificatePage />} />
              <Route path="/highlight/:token" element={<HighlightPage />} />
              <Route path="/dossier/:id" element={<Dossier />} />
              <Route path="/print/card/:side" element={<PrintCard />} />
              <Route path="/print/split" element={<SplitDemo />} />
            </Routes>
            <InstallBanner />
            <MobileErgonomics />
            <BottomTabBar />
            <ProfileGate />
          </BrowserRouter>
          </ErrorBoundary>
          <Toaster position="top-center" theme="dark" richColors />
        </ProgressProvider>
      </SettingsProvider>
    </PlayerAuthProvider>
  );
}

export default App;
